import { Link } from "@tanstack/react-router";
import { Crown, Sparkles } from "lucide-react";
import { trialProgress, type PremiumState } from "@/lib/premium";
import { useTranslation } from "react-i18next";

interface Props {
  state: PremiumState;
}

/**
 * Home-screen trial banner: "Day 1 of 3", remaining time and a progress bar.
 * Renders nothing unless a trial is actually running.
 */
export function TrialStatusCard({ state }: Props) {
  const { t } = useTranslation();
  const progress = trialProgress(state);
  if (!progress.active) return null;

  const urgent = progress.hoursRemaining <= 24;

  return (
    <div className="rounded-2xl border border-neon-yellow/40 bg-gradient-to-br from-neon-pink/10 via-purple-500/10 to-neon-blue/10 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Crown className="size-5 text-neon-yellow" aria-hidden="true" />
          <div>
            <p className="font-[family-name:var(--font-display)] text-sm font-extrabold text-ink">
              ⭐ Premium Trial
            </p>
            <p className="text-xs text-ink-muted">
              Day {progress.dayNumber} of {progress.daysTotal} · {progress.label}
            </p>
          </div>
        </div>
        <Link
          to="/subscription"
          className={`shrink-0 rounded-full px-3 py-1.5 text-[11px] font-bold ${
            urgent
              ? "bg-neon-pink text-white"
              : "border border-rule bg-card text-ink"
          }`}
        >
          {urgent ? "Keep Premium" : "Manage"}
        </Link>
      </div>
      <div
        className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress.percent}
        aria-label="Premium trial progress"
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-neon-pink to-neon-yellow transition-all"
          style={{ width: `${Math.min(100, Math.max(4, progress.percent))}%` }}
        />
      </div>
      {urgent && (
        <p className="mt-2 flex items-center gap-1 text-[11px] text-ink-muted">
          <Sparkles className="size-3" /> {t("monetization.premium_provides_50_daily_tutor_questions_and_full_study_access")}
        </p>
      )}
    </div>
  );
}